import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface CollapsibleSectionProps {
  title: string;
  /** Whether the section starts expanded. */
  defaultOpen?: boolean;
  /** Optional short description shown under the title. */
  description?: string;
  /** Light (beige), dark (scorecard page), or gold toggle bar. */
  variant?: "light" | "dark" | "gold";
  children: React.ReactNode;
}

/**
 * Collapsible card matching the analysis page styling. Used to present the
 * three blueprint sections (Scorecard, Summary of insights and actions,
 * Full analysis output) with the scorecard open by default.
 */
export function CollapsibleSection({
  title,
  defaultOpen = false,
  description,
  variant = "dark",
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const isDark = variant === "dark";
  const isGold = variant === "gold";
  return (
    <div
      className={
        isGold
          ? "rounded-lg border border-[#2a2a2a] bg-[#141414] overflow-hidden"
          : isDark
            ? "rounded-lg border border-[#2a2a2a] bg-[#141414] overflow-hidden"
            : "bg-beige-dark rounded-sm border border-border-light shadow-sm overflow-hidden"
      }
    >
      {isGold ? (
        <div className="px-4 py-4 md:px-6 md:py-5">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="w-full flex items-center justify-between gap-4 rounded-full px-4 py-3 md:px-6 md:py-3.5 text-left bg-gold-button hover:bg-gold-light transition-colors"
          >
            <div className="min-w-0">
              <h2 className="text-sm md:text-base font-sans font-semibold text-neutral-700">
                {title}
              </h2>
              {description && (
                <p className="mt-0.5 text-xs md:text-sm font-sans text-neutral-600">{description}</p>
              )}
            </div>
            <span className="shrink-0 text-neutral-700">
              {open ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </span>
          </button>
        </div>
      ) : (
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={
          isDark
              ? "w-full flex items-center justify-between gap-4 p-6 md:px-8 text-left hover:bg-[#1a1a1a] transition-colors"
              : "w-full flex items-center justify-between gap-4 p-6 md:px-8 text-left hover:bg-beige transition-colors"
        }
      >
        <div>
          <h2
            className={
              isDark
                  ? "text-xl font-sans font-semibold text-white"
                  : "text-2xl font-serif font-semibold text-text-dark"
            }
          >
            {title}
          </h2>
          {description && (
            <p
              className={
                isDark
                    ? "mt-1 text-sm font-sans text-white/50"
                    : "mt-1 text-sm font-sans text-text-dark opacity-70"
              }
            >
              {description}
            </p>
          )}
        </div>
        <span
          className={
            isDark
                ? "shrink-0 text-white/50"
                : "shrink-0 text-text-dark opacity-70"
          }
        >
          {open ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </span>
      </button>
      )}
      {open && (
        <div
          className={
            isGold || isDark
              ? "px-6 md:px-8 pb-8 pt-0 border-t border-[#2a2a2a]"
              : "px-6 md:px-8 pb-8 pt-0 border-t border-border-light"
          }
        >
          <div className={isGold || isDark ? "pt-6 text-white/80" : "pt-6"}>{children}</div>
        </div>
      )}
    </div>
  );
}
